import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  MaxLengthValidator,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { AddressValidatorService } from "../../address-validator/address-validator.service";
import { AddressData } from "../../../../../core/classes/address-data";
import { FOREIGN_PROVINCE } from "../../../../../core/enums/destination.enum";
import { FlowSendAreaEnum } from "../../../../../core/enums/flow-send-area.enum";
import {
  localeZipCodeValidators,
  LocalizationService,
  provinceValidators,
  zipCodeValidators
} from "../../../../../core/lib/localization.service";
import { PERSON_SPECIFICATION_TYPE, PersonSpecificationsConfiguration } from "./person-specifications.configuration";
import { CompanyCustomConfiguration, ProtocolSettings, SendCategorySender } from "../../../../../core/interfaces/company-custom-configuration";
import { SendCategoryService } from "../../../../../send-category.service";
import { RecipientData } from "../../../../../core/interfaces/recipient-data";
import { GroupSender } from "../../../../../core/classes/group-sender";
import { ISuggestedAddressCorrection } from "../../../../../core/interfaces/address-validation";
import { SpecialCharsService } from "../../../../../core/lib/special-chars.service";
import { FormService } from "../../../../../core/lib/form.service";
import { PersonSpecificationsControls } from "../../../../../core/interfaces/person-specifications.controls";
import { JwtService } from "src/app/core/lib/jwt.service";
import { AddressBookTypeEnum, AddressBookTypeLabelMapping } from "src/app/core/interfaces/address-book-response";
import { AddressBookService } from "src/app/pages/admin/address-book/address-book.service";
import { AddressBookContactResponse } from "src/app/core/interfaces/address-book-contact-response";
import { AddressBookContactParameter } from "src/app/core/classes/address-book-contact.parameter";
import { SendTypeSendCategoryEnum } from "src/app/core/interfaces/send-type-configuration";
import { MatLegacyDialog, MatLegacyDialogRef } from "@angular/material/legacy-dialog";
import { InfoDialogComponent } from "./dialog-info";
import { CompanyUiCustomizationService } from "src/app/core/lib/company-ui/company.ui-customization.service";

export const personSpecificationsDefaultValidators = () => [Validators.required, Validators.maxLength(100)];
const personSpecificationsEmailValidators = () => [Validators.email];
const personSpecificationsSmsSenderValidators = () => [Validators.minLength(4), Validators.maxLength(11)];
const emailUserNamePattern = /^[a-zA-Z0-9._+-]+$/;

@Component({
  selector: "app-person-specifications",
  templateUrl: "./person-specifications.component.html",
  styleUrls: ["./person-specifications.component.scss"],
  providers: [SpecialCharsService, LocalizationService]
})
export class PersonSpecificationsComponent implements OnInit {

  @Input() configuration!: PersonSpecificationsConfiguration;

  form: FormGroup;
  protocolSetting: ProtocolSettings | null = null;
  addressBookContactConfiguration: AddressBookContactParameter | null = null;
  disableAddressBookButton: boolean = false;
  disableDialogInfo: boolean = false;
  BPERGroupUser: boolean = false;

  /**
   * Modalità "lista mittenti": quando true, il campo Email viene sostituito da una mat-select
   * con la lista dei sender Readonly=true configurati per la SendCategory effettiva del flow corrente.
   */
  senderSelectMode: boolean = false;

  /** Opzioni della select mittente (solo i Readonly=true della SendCategory effettiva del flow corrente). */
  senderSelectOptions: SendCategorySender[] = [];

  constructor(
    private _fb: FormBuilder,
    private _validator: AddressValidatorService,
    private sendCategoryService: SendCategoryService,
    private formService: FormService,
    public localizationService: LocalizationService,
    public jwtService: JwtService,
    public specialCharsService: SpecialCharsService,
    private addressBookService: AddressBookService,
    private dialog: MatLegacyDialog,
    private companyUiService: CompanyUiCustomizationService) {

    this.form = this._fb.group({
      Name: new FormControl(null, personSpecificationsDefaultValidators()),
      Address: new FormControl(null, personSpecificationsDefaultValidators()),
      ZipCode: new FormControl(null, localeZipCodeValidators()),
      City: new FormControl(null, personSpecificationsDefaultValidators()),
      Province: new FormControl(null, provinceValidators()),
      Email: new FormControl(null, personSpecificationsEmailValidators()),
      Phone: new FormControl(null),
      Country: new FormControl(null),
      Protocol: new FormControl(null, Validators.maxLength(100)),
      // Modalità SELECT: control valorizzato col sender scelto dall'utente. Disabled di default,
      // viene abilitato solo quando il componente entra in senderSelectMode.
      SenderSelection: new FormControl({ value: null, disabled: true })
    });

    this.form
      .valueChanges
      .subscribe((formValue) => {

        let zipCode = formValue.ZipCode;
        let city = formValue.City;

        // Verifica se il campo "City" contiene esattamente 5 cifre numeriche
        if (/^\d{5}$/.test(city)) {

          this.form.patchValue({ ZipCode: city, City: null }, { emitEvent: false });

        }

        // Verifica se il campo "ZipCode" contiene spazi vuoti e se lo zipcode è italiano
        if (zipCode && /\s/.test(zipCode) && this.configuration.localAreaConfiguration) {

          this.form.patchValue({ ZipCode: zipCode.replace(/\s/g, "") }, { emitEvent: false });

        }

        this.configuration
          .setVerified(null);

      });

  }

  async ngOnInit() {
    this.BPERGroupUser = await this.companyUiService.BPERGroupUser();
    const addressBookEnabled = await this.jwtService.getAddressBookEnabled();




    if (addressBookEnabled) {

      this.addressBookContactConfiguration = AddressBookContactParameter.create()

      if (this.configuration.personSpecificationType === PERSON_SPECIFICATION_TYPE.RECIPIENT) {

        this.addressBookContactConfiguration
          .setAddressBookType(AddressBookTypeEnum.Recipient);

      } else (

        this.addressBookContactConfiguration
          .setAddressBookType(AddressBookTypeEnum.Sender)

      )


    }

    await this.specialCharsService.loadConfig();
    await this.setProtocol();

    await this.localizationService
      .loadConfig(<PersonSpecificationsControls>{
        ZipCodeControl: this.form.get("ZipCode"),
        CityControl: this.form.get("City"),
        ProvinceControl: this.form.get("Province"),
        CountriesControl: this.form.get("Country")
      });

  }

  get emailControlEnabled(): boolean {
    return (
      (this.configuration.flow?.emailStandaloneChannelSetupEnabled ?? false)
      ||
      ((this.configuration.flow?.OTPLetterStandaloneChannelSetupEnabled ?? false) && this.configuration.senderContainer) // se sono canale digitale Raccomandata OTP e sono nel form mittente
    );



  }



  get phoneControlEnabled(): boolean {

    return (
      ((this.configuration.flow?.smsStandaloneChannelSetupEnabled ?? false) && this.configuration.recipientContainer) // se sono canale digitale sms e sono nel form destinataro
      ||
      (this.configuration.flow?.OTPLetterStandaloneChannelSetupEnabled ?? false) && this.configuration.recipientContainer); // se sono canale digitale Raccomandata OTP

  }

  openInfoDialog() {

    this.dialog.open(InfoDialogComponent);

  }

  ShowInfoButton(): boolean {

    return (
      this.configuration?.title === AddressBookTypeLabelMapping[AddressBookTypeEnum.Recipient].toUpperCase() &&
      this.configuration.flow?.SendTypeConfigurationSendTypeSendCategory === SendTypeSendCategoryEnum.PEC && this.BPERGroupUser
    );

  }

  protected getPlaceholder(): string {

    switch (this.configuration.flow?.SendTypeConfigurationSendTypeSendCategory) {


      case SendTypeSendCategoryEnum.Email:
        return "E-mail";

      case SendTypeSendCategoryEnum.PEC:
        return "PEC";

      default:
        return "E-mail";

    }



  }
  async setProtocol() {

    const companyCustomConfiguration: CompanyCustomConfiguration | null = await this.jwtService.getCompanyCustomConfiguration();

    if (companyCustomConfiguration == null || companyCustomConfiguration.ProtocolSettings == null) return;

    if (this.configuration.personSpecificationType == PERSON_SPECIFICATION_TYPE.RECIPIENT) {

      this.protocolSetting = companyCustomConfiguration.ProtocolSettings;

    }

  }

  updateCurrentFlowValidators() {

    // Regola: per il container Sender sui canali Email/PEC il pulsante "Scegli contatto" della rubrica
    // non deve mai comparire, a prescindere dalla presenza/forma dei SendCategorySenders configurati.
    // Set additivo: i rami readonly/SELECT di setNameDigitalConfiguration possono ancora settare true
    // anche su altri canali (es. SMS/OTPLetter) senza conflitti.
    const flowSendCategory = this.configuration.flow?.SendTypeConfigurationSendTypeSendCategory;
    if (
      this.configuration.senderContainer
      && (flowSendCategory === SendTypeSendCategoryEnum.Email || flowSendCategory === SendTypeSendCategoryEnum.PEC)
    ) {
      this.disableAddressBookButton = true;
    }

    if (this.configuration.flow?.digitalSendingStandaloneChannelSetupEnabled) {

      // canali digitali non ho bisogno dei dati di indirizzo
      ["Address", "ZipCode", "City", "Province"].forEach(key => this.formService.clearControlValidators(this.form.get(key)));

    } else {

      let addressValidator: ValidatorFn[] = personSpecificationsDefaultValidators();

      // per tnotice la lunghezza dell'indirizzo è 30 caratteri
      if (this.configuration.flow?.OTPLetterStandaloneChannelSetupEnabled) {

        addressValidator = [Validators.required, Validators.maxLength(30)];

      }

      this.form.get("Address")?.setValidators(addressValidator);
      this.form.get("ZipCode")?.setValidators(localeZipCodeValidators());
      this.form.get("City")?.setValidators(personSpecificationsDefaultValidators());
      this.form.get("Province")?.setValidators(provinceValidators());

    }

    if (this.protocolSetting && this.protocolSetting.Mandatory == true) {

      this.setupProtocolValidator();

    }

    this.setupEmailValidators();

    this.setupPhoneValidators();

    this.setupNameValidators();

    this.form.updateValueAndValidity();

  }

  setNamePatternConfiguration(senderPrefix: string) {

    const regex = `^${senderPrefix}.{0,44}$`;

    const PATTERN_VALIDATOR = Validators.pattern(new RegExp(regex));
    const LENGTH_VALIDATOR: ValidatorFn = Validators.maxLength(senderPrefix.length + 44);

    this.configuration.setErrors({ pattern: `Attenzione: ricordati che il mittente deve sempre iniziare con "${senderPrefix}" seguito da un testo libero di massimo 44 caratteri.` });

    this.setupNameValidators([PATTERN_VALIDATOR, LENGTH_VALIDATOR]);

  }

  setNameDigitalConfiguration(sendCategorySenders: SendCategorySender[]) {

    const form = this.form;

    // Resetto sempre eventuali residui di una precedente attivazione di senderSelectMode
    // (es. cambio flow Email <-> PEC, o ritorno indietro di step).
    this.resetSenderSelectMode();

    // Determino se per la SendCategory effettiva del flow corrente vada attivata la modalità SELECT.
    const resolution = this.resolveSendersForCategory(sendCategorySenders);

    if (resolution.mode === "select" && resolution.preSelected) {

      // Caso A (>=2 sender Readonly): la select sostituisce il campo Email.
      this.applySenderSelectMode(resolution.options, resolution.preSelected);

      form.updateValueAndValidity();

      return;

    }

    // Casi B/C (comportamento esistente): se nella lista filtrata per la SendCategory esatta del flow
    // esiste un Default=true, ha la precedenza; altrimenti fallback alla logica originale che prende
    // il primo match (la `getCategorySenderByFlow` ignora il flag Default).
    const sender: SendCategorySender | null = resolution.classicPreferred
      ?? this.sendCategoryService.getCategorySenderByFlow(this.configuration.flow, sendCategorySenders);

    if (sender) {

      if (sender.Sender) {

        form.get("Name")?.setValue(sender.Sender);
        if (sender.Readonly) form.get("Name")?.disable();

        if (sender.Readonly && this.addressBookContactConfiguration?.AddressBookType === AddressBookTypeEnum.Sender) {
          this.disableAddressBookButton = true;
        }

      }

      if (sender.Phone) {

        form.get("Phone")?.setValue(sender.Phone);
        if (sender.Readonly) form.get("Phone")?.disable();

      }

      if (sender.Email) {

        const emailControl = form.get("Email");

        if (sender.Readonly) {

          emailControl?.setValue(sender.Email);
          emailControl?.disable();

        } else {

          const [username, provider] = sender.Email.split("@");

          if (username && !emailControl?.value) emailControl?.setValue(username);

          if (provider) this.configuration.setEmailProvider(`@${provider}`);

        }

        this.setupEmailValidators();

      }

    } else {

      form.get("Name")?.enable();
      form.get("Phone")?.enable();
      form.get("Email")?.enable();

    }

    form.updateValueAndValidity();

  }

  private setupNameValidators(additionalValidators: ValidatorFn[] = []) {

    const nameControl = this.form.get("Name");

    let NAME_VALIDATORS = personSpecificationsDefaultValidators();

    // se sono mittente di un sms
    if (this.configuration.flow?.smsStandaloneChannelSetupEnabled && this.configuration.senderContainer) {

      NAME_VALIDATORS = NAME_VALIDATORS.concat(...personSpecificationsSmsSenderValidators());

    }

    if (additionalValidators.length) {

      NAME_VALIDATORS = NAME_VALIDATORS.concat(...additionalValidators);

    }

    nameControl?.setValidators(NAME_VALIDATORS);

  }

  private setupEmailValidators() {

    const emailControl = this.form.get("Email");

    if (this.configuration.emailProvider) { // non devo più verificare se equivale ad una email, al massimo verificare che non ci siano campi speciali

      emailControl
        ?.setValidators([Validators.pattern(emailUserNamePattern), Validators.required]);

    } else {

      const emailValidators = personSpecificationsEmailValidators();

      if (this.emailControlEnabled) {

        emailValidators
          .push(Validators.required);

      }

      emailControl
        ?.setValidators(emailValidators);

    }

    emailControl?.updateValueAndValidity();

  }

  private setupProtocolValidator() {

    const protocolControl = this.form.get("Protocol");

    const protocolValidators = personSpecificationsDefaultValidators();

    protocolControl
      ?.setValidators(protocolValidators);

    protocolControl?.updateValueAndValidity();

    this.form.updateValueAndValidity(); //forzo il refresh perché senno non prende i validator su spedizioni esistenti


  }

  private setupPhoneValidators() {

    const phoneControl = this.form.get("Phone");

    const phoneValidators = [];

    if (this.phoneControlEnabled) {

      phoneValidators
        .push(Validators.required);

    }

    phoneControl?.setValidators(phoneValidators);

    phoneControl?.updateValueAndValidity();

  }

  /**
   * It opens a dialog with a form that validates the data in the form
   */
  validate() {

    this._validator
      .openValidator(this.configuration.title ?? "User Validation", <AddressData>this.form.value)
      .afterClosed()
      .subscribe((result: AddressData | null) => {

        if (result) {
          this.setFormValue(result);
        }

      });

  }

  /* A function that sets the form values. */
  setFormValue(address: RecipientData | GroupSender | AddressData | ISuggestedAddressCorrection) {

    address.Name = address.Name ?? this.form.get("Name")?.value; // serve a tenere il nome; il validatore quando normalizza l'indirizzo mi restituisce null per il nome

    if (!!this.configuration.emailProvider && (address.Email ?? "").indexOf("@") >= 0) {

      const [username] = (address.Email ?? "").split("@");

      if (username) address.Email = username;

    }

    this.form.patchValue(address);

    this.form.updateValueAndValidity();

    this.configuration
      .setVerified(this.form.valid); // arrivato a questo punto sto settando i valori dal validatore o da una get, sono dunque validi

  };

  /**
   * If the user has selected a foreign country, then add the zip code validators and set the province to "Foreign"
   */
  private handleForeignAddressValidations() {

    this.form.get("Country")?.setValidators([this.localizationService.autocompleteStringValidator(this.localizationService.countries ?? []), ...personSpecificationsDefaultValidators()]);
    this.form.get("ZipCode")?.setValidators(zipCodeValidators());
    this.form.get("Province")?.setValue(FOREIGN_PROVINCE);

    this.form.updateValueAndValidity();

  }

  /**
   * If the country is not Italy, then the zip code is validated as a string of at least 5 characters, the province is set
   * to null, and the country is removed from the form
   */
  private resetLocalAddressValidations() {

    this.form.get("ZipCode")?.setValidators(localeZipCodeValidators());
    this.form.get("Province")?.setValue(null);

    this.form.get("Country")?.setValidators(null);
    this.form.get("Country")?.setValue(null);

    this.form.updateValueAndValidity();

  }

  toggleDestinationValidation(sendArea: FlowSendAreaEnum) {

    this.configuration
      .setSendArea(sendArea);

    if (this.configuration.externalAreaConfiguration) {

      this.handleForeignAddressValidations();

    } else {

      this.resetLocalAddressValidations();

    }

  }

  selectContact() {

    this.addressBookService.selectContact(this.addressBookContactConfiguration).afterClosed()
      .subscribe((contact: AddressBookContactResponse | null) => {


        if (contact) {

          this.form.patchValue({
            Name: contact.Name,
            Address: contact.Address,
            ZipCode: contact.ZipCode,
            City: contact.City,
            Province: contact.Province,
            Country: contact.Country,
            Email: contact.Email,
            Phone: contact.Phone
          });

          if(this.configuration.flow?.SendTypeConfigurationSendTypeSendCategory === SendTypeSendCategoryEnum.PEC) {

            this.form.patchValue({
              Email: contact.CertifiedEmail,
            });

          }

          this.form.markAllAsTouched();

          this.configuration.setVerified(this.form.valid);

        }



      });

  }

  /**
   * Determina come va proposto il mittente per la SendCategory effettiva del flow corrente,
   * sulla base dei flag Readonly/Default presenti nei sender filtrati.
   *
   * Regole:
   *  - SELECT se esiste almeno un sender con Readonly=true && Default=true
   *    E la lista dei Readonly=true contiene almeno 2 elementi.
   *  - In ogni altro caso "classic". In aggiunta, se nella lista filtrata esiste un sender con
   *    Default=true (qualunque valore di Readonly), questo viene restituito come `classicPreferred`
   *    e ha la precedenza sul sender ricavato dal {@link SendCategoryService.getCategorySenderByFlow}
   *    (che ignora il flag Default e prende il primo match).
   *
   * Casistiche coperte:
   *  - D+R=true presente con altri R=true                                  → SELECT, pre-selezionato il D+R=true
   *  - solo D=true (R=false)                                               → classic editabile su quel D=true
   *  - solo D+R=true singolo                                               → classic readonly singolo su quel D+R=true
   *  - D+R=false e D+R=true insieme + altri R=true                         → SELECT, vince il D+R=true
   *  - N R=true con D=false + 1 R=false con D=true                         → classic editabile sul D=true (gli R=true non si considerano)
   *  - nessun D=true                                                       → classic con fallback alla logica esistente
   *
   * Nota su Default duplicati (configurazione errata lato BE): in caso di più sender D=true
   * viene preso il primo trovato in ordine di iterazione.
   */
  private resolveSendersForCategory(allSenders: SendCategorySender[]): {
    mode: "select" | "classic";
    options: SendCategorySender[];
    preSelected: SendCategorySender | null;
    classicPreferred: SendCategorySender | null;
  } {

    if (!this.configuration.flow) return { mode: "classic", options: [], preSelected: null, classicPreferred: null };

    const filtered = this.sendCategoryService.getCategorySendersByFlow(this.configuration.flow, allSenders);
    // Confronti truthy (non strict-equality) per essere tolleranti a payload con flag stringa/null/undefined,
    // coerentemente con il resto del componente (es. `if (sender.Readonly)` nel ramo classic).
    const preferredReadonlyDefault = filtered.find(s => !!s.Default && !!s.Readonly);
    const readonlyList = filtered.filter(s => !!s.Readonly);

    if (preferredReadonlyDefault && readonlyList.length >= 2) {

      return { mode: "select", options: readonlyList, preSelected: preferredReadonlyDefault, classicPreferred: null };

    }

    // Sender preferito per il ramo classic: priorità al Default+Readonly=true (anche se singolo,
    // quindi non sufficiente ad attivare la SELECT); in fallback, il primo Default=true qualunque Readonly.
    const classicPreferred = preferredReadonlyDefault
      ?? filtered.find(s => !!s.Default)
      ?? null;

    return { mode: "classic", options: [], preSelected: null, classicPreferred };

  }

  /**
   * Attiva la modalità SELECT: il campo Email viene sostituito da una mat-select con la lista
   * dei sender Readonly disponibili. Name ed Email vengono valorizzati dal sender pre-selezionato
   * e disabilitati. La select rimane abilitata: l'utente può cambiare scelta in qualsiasi momento.
   */
  private applySenderSelectMode(options: SendCategorySender[], preSelected: SendCategorySender) {

    const form = this.form;

    this.senderSelectMode = true;
    this.senderSelectOptions = options;

    // Abilito e valorizzo il control della select con il sender pre-selezionato.
    const selectionControl = form.get("SenderSelection");
    selectionControl?.enable({ emitEvent: false });
    selectionControl?.setValidators([Validators.required]);
    selectionControl?.setValue(preSelected, { emitEvent: false });
    selectionControl?.updateValueAndValidity({ emitEvent: false });

    // Name: valorizzato dal sender pre-selezionato e disabilitato (deriva sempre dalla select).
    if (preSelected.Sender) {

      form.get("Name")?.setValue(preSelected.Sender);
      form.get("Name")?.disable();

    }

    // Email: valorizzata dal sender pre-selezionato e disabilitata (deriva sempre dalla select).
    if (preSelected.Email) {

      form.get("Email")?.setValue(preSelected.Email);
      form.get("Email")?.disable();

    }

    // Phone: replico il comportamento del ramo readonly classico (set + disable se presente).
    if (preSelected.Phone) {

      form.get("Phone")?.setValue(preSelected.Phone);
      form.get("Phone")?.disable();

    }

    // Coerentemente con il ramo readonly classico, nascondo il pulsante "Scegli contatto" sul mittente.
    if (this.addressBookContactConfiguration?.AddressBookType === AddressBookTypeEnum.Sender) {

      this.disableAddressBookButton = true;

    }

    // Mantengo allineati i validator dell'Email per coerenza con il ramo readonly classico
    // (il control è disabled, ma se in futuro venisse riabilitato i validator sarebbero pronti).
    this.setupEmailValidators();

  }

  /**
   * Ripristina lo stato non-SELECT del componente. Va invocato all'inizio di ogni configurazione
   * mittente per evitare residui di una precedente attivazione (es. cambio flow Email <-> PEC).
   */
  private resetSenderSelectMode() {

    this.senderSelectMode = false;
    this.senderSelectOptions = [];

    const selectionControl = this.form.get("SenderSelection");

    if (selectionControl) {

      selectionControl.clearValidators();
      selectionControl.setValue(null, { emitEvent: false });
      selectionControl.disable({ emitEvent: false });
      selectionControl.updateValueAndValidity({ emitEvent: false });

    }

  }

  /**
   * Handler invocato dall'HTML al cambio di selezione nella mat-select.
   * Aggiorna Name, Email (e Phone se presente sul sender) tenendo la select abilitata
   * per consentire ulteriori cambi di scelta.
   */
  onSenderSelectionChange(sender: SendCategorySender) {

    if (!sender) return;

    const form = this.form;

    if (sender.Sender) {

      form.get("Name")?.setValue(sender.Sender);
      form.get("Name")?.disable();

    }

    if (sender.Email) {

      form.get("Email")?.setValue(sender.Email);
      form.get("Email")?.disable();

    }

    // Phone: aggiorno solo se il nuovo sender lo specifica, per non sovrascrivere
    // un eventuale valore precedente. NB: in modalità SELECT il control Phone è di norma
    // nascosto (phoneControlEnabled è false per Email/PEC sul mittente), ma mantengo
    // l'allineamento per coerenza col ramo readonly classico.
    if (sender.Phone) {

      form.get("Phone")?.setValue(sender.Phone);
      form.get("Phone")?.disable();

    }

    form.updateValueAndValidity();

  }

  //#endregion

}